import { FEATURE_FLAG_ACTIONS, FEATURE_FLAG_SUBJECTS } from '@/features/Casl';
import { useAbilityContext } from '@/features/Casl/hooks';
import { Button, Modal, Typography } from '@analytical-alley/ui';
import { AiOutlineInfoCircle } from 'react-icons/ai';

export interface WelcomeModalProps {
    showModal: boolean;
    onClose: () => void;
    userName?: string;
}

export const WelcomeModal = ({
    showModal,
    onClose,
    userName,
}: WelcomeModalProps) => {
    const { ability, abilityInitialized } = useAbilityContext();

    const views: {
        title: string;
        description: string;
        subject:
            | FEATURE_FLAG_SUBJECTS.MODEL_CONTRIBUTION_DASHBOARD
            | FEATURE_FLAG_SUBJECTS.SPEND_SUMMARY_DASHBOARD
            | FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD
            | FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD
            | FEATURE_FLAG_SUBJECTS.EXECUTIVE_SUMMARY_DASHBOARD
            | FEATURE_FLAG_SUBJECTS.COMPETITORS_MEDIA_DASHBOARD
            | FEATURE_FLAG_SUBJECTS.REPORTS_DASHBOARD;
    }[] = [
        {
            title: 'Model Contributions view:',
            description:
                'Break down performance by category and variable to reveal the key drivers behind your results.',
            subject: FEATURE_FLAG_SUBJECTS.MODEL_CONTRIBUTION_DASHBOARD,
        },
        {
            title: 'Spend Summary view:',
            description:
                'View marketing spend and returns across channels and categories, with a unified ROI perspective.',
            subject: FEATURE_FLAG_SUBJECTS.SPEND_SUMMARY_DASHBOARD,
        },
        {
            title: 'Yearly Budget Optimization view:',
            description:
                'Set your annual budget or target KPI, and the built-in optimizer calculates channel-level allocations to maximize returns.',
            subject: FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD,
        },
        {
            title: 'Campaign Optimization view:',
            description:
                'Define your campaign scope by setting dates and targets, and the built-in optimizer calculates channel-level allocations with detailed monthly, weekly, and daily plans to maximize returns.',
            subject: FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD,
        },
        {
            title: 'Executive Summary view:',
            description:
                'Get a high-level overview of the key metrics and outcomes across your marketing performance in a single, consolidated view.',
            subject: FEATURE_FLAG_SUBJECTS.EXECUTIVE_SUMMARY_DASHBOARD,
        },
        {
            title: "Competitor's view:",
            description:
                'Analyze competitor spend, seasonal trends, and their impact on your business performance.',
            subject: FEATURE_FLAG_SUBJECTS.COMPETITORS_MEDIA_DASHBOARD,
        },
        {
            title: 'Reports view:',
            description:
                'Access all model-related files and shared documents in one central location.',
            subject: FEATURE_FLAG_SUBJECTS.REPORTS_DASHBOARD,
        },
    ];

    const enabledViews = abilityInitialized
        ? views.filter((view) =>
              ability.can(FEATURE_FLAG_ACTIONS.VIEW, view.subject),
          )
        : views;

    return (
        <Modal
            show={showModal}
            onClose={onClose}
            className=""
            size="3xl"
            dismissible={false}
        >
            <Modal.Header>
                <div className="flex items-center gap-3 pt-6">
                    <Typography variant="titleM" color="white">
                        Welcome to your Marketing Intelligence Platform
                        {userName ? `, ${userName}` : ''}!
                    </Typography>
                </div>
            </Modal.Header>
            <Modal.Body className="overflow-y-auto max-h-[calc(100vh-16rem)]">
                <div className="flex flex-col gap-6">
                    <Typography variant="bodyM" className="opacity-90">
                        This is your central environment for understanding,
                        exploring, and optimizing your marketing performance.
                        Here you’ll find your model results, ranging from
                        high-level overviews to detailed breakdowns of the
                        factors driving your business outcomes. Combined with a
                        high-precision planning and optimization tool, the
                        platform helps you move from insight to action faster
                        than ever.
                    </Typography>

                    <div className="flex flex-col gap-3 p-4 glass rounded-lg">
                        <Typography
                            variant="titleS"
                            color="white"
                            className="mb-2"
                        >
                            Your toolkit:
                        </Typography>
                        <ul className="list-disc list-inside marker:text-white space-y-2 opacity-90">
                            {enabledViews.map((view, index) => (
                                <li key={index}>
                                    <Typography
                                        variant="bodyM"
                                        color="white"
                                        className="inline"
                                    >
                                        <strong>{view.title}</strong>{' '}
                                        {view.description}
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex flex-col gap-3">
                        <Typography
                            variant="titleS"
                            color="white"
                            className="mb-2"
                        >
                            Using Contextual Help
                        </Typography>
                        <Typography
                            variant="bodyM"
                            color="white"
                            className="opacity-90"
                        >
                            For deeper understanding, look for the{' '}
                            <AiOutlineInfoCircle className="inline" /> icons
                            throughout the dashboard! Hovering over these will
                            activate <strong>Tooltips</strong>, which provide
                            instant definitions, contextual details, and
                            explanations for all displayed metrics.
                        </Typography>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="py-4">
                <Button
                    className="ml-auto"
                    color="light"
                    size="sm"
                    onClick={onClose}
                >
                    Get Started
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
